import { BlogPost } from "@/types/blog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/blogUtils";
import { Link } from "react-router-dom";
import { isSSR } from "@/lib/isSSR";
import { cn } from "@/lib/utils";
import { decodeHtmlEntities } from "@/utils/htmlUtils";

interface ServerFeaturedPostProps {
  post: BlogPost;
}

/**
 * Server-side safe version of FeaturedPost
 * This component is optimized for server-side rendering and ensures
 * that featured post content is fully rendered in the initial HTML without
 * client-side animations, effects, or state
 */
export function ServerFeaturedPost({ post }: ServerFeaturedPostProps) {
  const {
    title,
    featuredImage,
    excerpt,
    date,
    categories = [],
    slug,
  } = post;
  
  // Process excerpt to remove HTML tags and decode entities
  const cleanExcerpt = decodeHtmlEntities(excerpt.replace(/<[^>]+>/g, ''));
  
  // Format card date
  const formattedDate = formatDate(date);
  
  // Get primary category (first one)
  const primaryCategory = categories && categories.length > 0 ? categories[0] : null;
  
  // URL for the post
  const postUrl = `/blog/${slug}`;
  
  return (
    <Card className="overflow-hidden border-metal-light/20">
      <div className="grid grid-cols-1 md:grid-cols-2 h-full">
        <div className="relative h-64 md:h-full">
          {featuredImage ? (
            <img
              src={featuredImage}
              alt={title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted">
              <span className="text-muted-foreground text-sm">Immagine non disponibile</span>
            </div>
          )}
          
          {primaryCategory && (
            <div className="absolute bottom-4 left-4">
              <Badge variant="secondary" className="bg-white/80 text-primary">
                {primaryCategory}
              </Badge>
            </div>
          )}
        </div>
        
        <CardContent className="p-6 flex flex-col justify-center">
          <div className="text-sm text-muted-foreground mb-2">
            {formattedDate}
          </div>
          
          {isSSR() ? (
            <a href={postUrl} className="group">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                {title}
              </h2>
            </a>
          ) : (
            <Link to={postUrl} className="group">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                {title}
              </h2>
            </Link>
          )}
          
          <p className="text-muted-foreground mb-6 line-clamp-3">
            {cleanExcerpt}
          </p>
          
          <div>
            {isSSR() ? (
              <a 
                href={postUrl}
                className={cn(
                  "inline-flex items-center font-medium",
                  "text-primary hover:underline"
                )}
              >
                Leggi l'articolo completo
                <span className="ml-1">→</span>
              </a>
            ) : (
              <Link 
                to={postUrl}
                className={cn(
                  "inline-flex items-center font-medium",
                  "text-primary hover:underline"
                )}
              >
                Leggi l'articolo completo
                <span className="ml-1">→</span>
              </Link>
            )}
          </div>
        </CardContent>
      </div>
    </Card>
  );
}
